import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Lock, ShieldCheck } from "lucide-react";
import { Card } from "../ui/Card";
import { Select } from "../ui/Select";
import { DataTable, type Column } from "../ui/DataTable";
import { TableExportToolbar } from "../TableExportToolbar";
import { ReportsPanel } from "../bank/ReportsPanel";
import { AccountantPackagePanel } from "../bank/AccountantPackagePanel";
import { eur } from "../../lib/format";
import api from "../../lib/api";
import type { OverviewBucket, OverviewGranularity, OverviewResult, OverviewSource, MonthSummary } from "../../types/bank";

const now = new Date();
const YEARS = Array.from({ length: 6 }, (_, i) => now.getUTCFullYear() - i + 1);

const GRANULARITIES: { key: OverviewGranularity; label: string }[] = [
  { key: "year", label: "Jaar" },
  { key: "quarter", label: "Kwartaal" },
  { key: "month", label: "Maand" },
  { key: "day", label: "Dag" },
];

const SOURCES: { key: OverviewSource; label: string; color: string }[] = [
  { key: "facturen", label: "Facturen", color: "#3b82f6" },
  { key: "uitgaven", label: "Uitgaven", color: "#f59e0b" },
  { key: "bank", label: "Bank", color: "#10b981" },
  { key: "kasboek", label: "Kasboek", color: "#8b5cf6" },
];

const MONTHS_SHORT = ["Jan", "Feb", "Mrt", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];

type RapportenRow = OverviewBucket;

/** Totaalplaatje: inkomsten + uitgaven uit alle bronnen per jaar/kwartaal/maand/
 *  dag, met grafiek, bron-filters, maandafsluiting-status en de klassieke
 *  rapporten (W&V, cashflow, BTW, balans) + accountant-pakket. */
export function RapportenPanel({ kasboek }: { kasboek: boolean }) {
  const [year, setYear] = useState(now.getUTCFullYear());
  const [granularity, setGranularity] = useState<OverviewGranularity>("month");
  const [dayMonth, setDayMonth] = useState(now.getUTCMonth() + 1);
  const [chartType, setChartType] = useState<"bar" | "line">("bar");
  const [activeSources, setActiveSources] = useState<Set<OverviewSource>>(
    () => new Set<OverviewSource>(kasboek ? ["facturen", "uitgaven", "bank", "kasboek"] : ["facturen", "uitgaven", "bank"])
  );
  const [overview, setOverview] = useState<OverviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [visibleRows, setVisibleRows] = useState<RapportenRow[]>([]);
  const [monthSummary, setMonthSummary] = useState<MonthSummary[]>([]);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, unknown> = { year, granularity };
    if (granularity === "day") {
      params.from = dayMonth;
      params.to = dayMonth;
    }
    params.sources = Array.from(activeSources).join(",");
    api
      .get("/api/reports/overview", { params })
      .then((res) => setOverview(res.data))
      .finally(() => setLoading(false));
    api.get("/api/bank-transactions/summary", { params: { year } }).then((res) => setMonthSummary(res.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, granularity, dayMonth, activeSources, kasboek]);

  const toggleSource = (s: OverviewSource) => {
    setActiveSources((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  // Kolommen afhankelijk van de actieve bronnen; accessors geven ruwe
  // getallen (Excel-vriendelijk), render formatteert.
  const columns: Column<RapportenRow>[] = useMemo(() => {
    const cols: Column<RapportenRow>[] = [
      { key: "label", header: "Periode", accessor: (r) => r.label },
    ];
    if (activeSources.has("facturen")) {
      cols.push({
        key: "factuurOmzet", header: "Facturen", accessor: (r) => r.factuurOmzet,
        render: (r) => eur(r.factuurOmzet), className: "text-right",
      });
    }
    if (activeSources.has("uitgaven")) {
      cols.push({
        key: "kostenUitgaven", header: "Uitgaven", accessor: (r) => r.kostenUitgaven,
        render: (r) => eur(r.kostenUitgaven), className: "text-right",
      });
    }
    if (activeSources.has("bank")) {
      cols.push(
        {
          key: "bankIn", header: "Bank in", accessor: (r) => r.bankIn,
          render: (r) => eur(r.bankIn), className: "text-right",
        },
        {
          key: "bankUit", header: "Bank uit", accessor: (r) => r.bankUit,
          render: (r) => eur(r.bankUit), className: "text-right",
        },
      );
    }
    if (activeSources.has("kasboek")) {
      cols.push(
        {
          key: "kasIn", header: "Kas in", accessor: (r) => r.kasIn,
          render: (r) => eur(r.kasIn), className: "text-right",
        },
        {
          key: "kasUit", header: "Kas uit", accessor: (r) => r.kasUit,
          render: (r) => eur(r.kasUit), className: "text-right",
        },
      );
    }
    cols.push(
      {
        key: "geldstroomIn", header: "Totaal in", accessor: (r) => r.geldstroomIn,
        render: (r) => <span className="font-semibold text-emerald-600">{eur(r.geldstroomIn)}</span>,
        className: "text-right",
      },
      {
        key: "geldstroomUit", header: "Totaal uit", accessor: (r) => r.geldstroomUit,
        render: (r) => <span className="font-semibold text-rose-600">{eur(r.geldstroomUit)}</span>,
        className: "text-right",
      },
      {
        key: "nettoGeldstroom", header: "Netto", accessor: (r) => r.nettoGeldstroom,
        render: (r) => eur(r.nettoGeldstroom), className: "text-right",
      },
      {
        key: "nettoBoekhoudkundig", header: "Resultaat (boekh.)", accessor: (r) => r.nettoBoekhoudkundig,
        render: (r) => eur(r.nettoBoekhoudkundig), className: "text-right",
      },
      {
        key: "eindsaldoGeldstroom", header: "Eindsaldo", accessor: (r) => r.eindsaldoGeldstroom ?? 0,
        render: (r) => (r.eindsaldoGeldstroom != null ? eur(r.eindsaldoGeldstroom) : "—"),
        className: "text-right", sortable: false,
      },
    );
    return cols;
  }, [activeSources]);

  // Synthetische totaalrij (zodat export/print de totalen meenemen).
  const rowsWithTotal: RapportenRow[] = useMemo(() => {
    if (!overview) return [];
    const lastSaldo = [...overview.buckets].reverse().find((b) => b.eindsaldoGeldstroom != null)?.eindsaldoGeldstroom ?? null;
    return [
      ...overview.buckets,
      {
        ...overview.buckets[0],
        ...overview.totals,
        key: "__total__",
        label: "Totaal",
        quarter: undefined,
        month: undefined,
        day: undefined,
        eindsaldoGeldstroom: lastSaldo,
      },
    ];
  }, [overview]);

  const chartData = overview?.buckets ?? [];

  const donutIn = (overview?.verdelingPerBron ?? [])
    .filter((v) => activeSources.has(v.source))
    .map((v) => ({
      name: SOURCES.find((s) => s.key === v.source)?.label ?? v.source,
      value: v.in,
      fill: SOURCES.find((s) => s.key === v.source)?.color ?? "#94a3b8",
    }));
  const donutUit = (overview?.verdelingPerBron ?? [])
    .filter((v) => activeSources.has(v.source))
    .map((v) => ({
      name: SOURCES.find((s) => s.key === v.source)?.label ?? v.source,
      value: v.uit,
      fill: SOURCES.find((s) => s.key === v.source)?.color ?? "#94a3b8",
    }));

  const tooltipStyle = {
    borderRadius: "1rem",
    border: "1px solid rgba(226,232,240,0.8)",
    boxShadow: "0 10px 30px -10px rgba(15,23,42,0.15)",
  } as const;

  return (
    <div className="space-y-6">
      {/* Besturing */}
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={String(year)}
          onChange={(e) => setYear(Number(e.target.value))}
          options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
          className="w-28"
        />
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {GRANULARITIES.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setGranularity(g.key)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                granularity === g.key ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
        {granularity === "day" && (
          <Select
            value={String(dayMonth)}
            onChange={(e) => setDayMonth(Number(e.target.value))}
            options={MONTHS_SHORT.map((m, i) => ({ value: String(i + 1), label: m }))}
            className="w-28"
          />
        )}
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {([
            { k: "bar", label: "Staaf" },
            { k: "line", label: "Lijn" },
          ] as const).map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setChartType(t.k)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                chartType === t.k ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 ml-auto">
          {SOURCES.filter((s) => s.key !== "kasboek" || kasboek).map((s) => (
            <label key={s.key} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={activeSources.has(s.key)}
                onChange={() => toggleSource(s.key)}
                className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
              />
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      {/* Grafiek */}
      <Card className="p-6" title={`Inkomsten & uitgaven ${year}`} subtitle="Geldstroom per periode (bank + niet-geboekt + kasboek)">
        <div className="h-72 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">Laden…</div>
          )}
          {!loading && chartType === "line" ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => `€ ${Number(v).toFixed(2)}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <Line type="monotone" dataKey="geldstroomIn" name="In" stroke="#10b981" strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="geldstroomUit" name="Uit" stroke="#f43f5e" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            !loading && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(16,185,129,0.06)" }} contentStyle={tooltipStyle} formatter={(v: any) => `€ ${Number(v).toFixed(2)}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Bar dataKey="geldstroomIn" name="In" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="geldstroomUit" name="Uit" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )
          )}
        </div>
      </Card>

      {/* Verdeling per bron (twee donuts) */}
      <div className="grid md:grid-cols-2 gap-6">
        {[
          { title: "Inkomsten per bron", data: donutIn },
          { title: "Uitgaven per bron", data: donutUit },
        ].map((d) => (
          <Card key={d.title} className="p-6" title={d.title}>
            <div className="h-56">
              {d.data.every((x) => !x.value) ? (
                <p className="text-sm text-slate-400 flex items-center justify-center h-full">Geen posten.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => `€ ${Number(v).toFixed(2)}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                    <Pie data={d.data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {d.data.map((x, i) => (
                        <Cell key={i} fill={x.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Totaaltabel */}
      <Card className="p-6" title="Totaalplaatje" subtitle="Per periode, met totalenrij — exporteerbaar als CSV, Excel of PDF">
        <DataTable
          rows={rowsWithTotal}
          columns={columns}
          rowKey={(r) => r.key}
          searchPlaceholder="Zoek periode..."
          storageKey="rapporten-totaalplaatje"
          emptyMessage={loading ? "Laden..." : "Geen data voor deze selectie."}
          onVisibleRowsChange={setVisibleRows}
          toolbar={
            <TableExportToolbar
              rows={visibleRows}
              columns={columns as Column<any>[]}
              filename={`totaalplaatje-${granularity}-${year}`}
              source="Rapporten"
              printTitle={`Totaalplaatje ${year}`}
            />
          }
        />
        <p className="text-xs text-slate-400 mt-3">
          Geldstroom = bank + ontvangen betalingen/niet-geboekte uitgaven + kasboek (geen dubbel telling). Resultaat (boekh.) = factuurregels + kas-omzet − aftrekbare uitgaven − kas-kosten.
        </p>
      </Card>

      {/* Maandafsluiting-status */}
      <Card className="p-6" title="Maandafsluiting" subtitle="Status van de periode-afsluitingen — gesloten maanden zijn geblokkeerd voor boekingen">
        <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
          {MONTHS_SHORT.map((m, i) => {
            const ms = monthSummary.find((x) => x.month === i + 1);
            const cls = ms?.locked
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : ms && ms.unmatched > 0
                ? "bg-amber-50 border-amber-200 text-amber-700"
                : "bg-slate-50 border-slate-200 text-slate-500";
            return (
              <div key={m} title={ms?.locked ? "Afgesloten" : ms && ms.unmatched > 0 ? `${ms.unmatched} niet gekoppeld` : "Open"} className={`rounded-xl border px-2 py-2 text-center text-xs font-semibold ${cls}`}>
                <Lock size={12} className="inline mr-1 -mt-0.5" />
                {m}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5">
          <ShieldCheck size={14} />
          Afsluiten of heropenen doe je in de bankmodule:{" "}
          <Link to="/banktransacties?tab=afsluiting" className="font-semibold text-brand-600 hover:text-brand-700 underline">
            Maandafsluiting openen
          </Link>
        </p>
      </Card>

      {/* Klassieke rapporten + accountant-pakket */}
      <Card className="p-6" title="Klassieke rapporten" subtitle="Winst & verlies, cashflow, BTW-aangifte en balans">
        <ReportsPanel />
      </Card>
      <Card className="p-6" title="Accountant-pakket" subtitle="Alles voor de accountant in één ZIP">
        <AccountantPackagePanel />
      </Card>
    </div>
  );
}